import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import apiService from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { compareFolderNames } from '@/utils/folderSort'

export interface FileItem {
  id: string
  name: string
  client_id: string
  folder_id?: string
  size: number
  mime_type: string
  accounting_status: 'pending' | 'in_review' | 'completed'
  accounting_processed_by_id?: string
  accounting_processed_at?: string
  accounting_notes?: string
  description?: string
  has_notes?: boolean
  last_downloaded_at?: string
  deleted_at?: string
  created_at: string
  updated_at: string
}

export interface Folder {
  id: string
  name: string
  client_id: string
  parent_folder_id?: string
  direct_files_count: number
  direct_folders_count: number
  deleted_at?: string
  created_at: string
  updated_at: string
  children?: Folder[]
}

export interface FileNote {
  id: string
  file_id: string
  author_id: string
  author_name: string
  text: string
  visibility: 'private_accountant'
  deleted_at?: string
  created_at: string
  updated_at?: string
}

export interface UploadProgress {
  fileId: string
  filename: string
  progress: number
  status: 'pending' | 'requesting' | 'uploading' | 'confirming' | 'processing' | 'completed' | 'error'
  error?: string
}

export interface Client {
  id: string
  name: string
  societate?: string
  cui?: string
  firm_id: string
}

export const useFilesStore = defineStore('files', () => {
  const files = ref<FileItem[]>([])
  const folders = ref<Folder[]>([])
  const currentFolder = ref<Folder | null>(null)
  const selectedFiles = ref<Set<string>>(new Set())
  const selectedFolders = ref<Set<string>>(new Set())
  const uploads = ref<Map<string, UploadProgress>>(new Map())
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Client selection for admin/accountant users
  const selectedClientId = ref<string | null>(null)
  const availableClients = ref<Client[]>([])
  const clientsLoading = ref(false)

  // Get auth store
  const authStore = useAuthStore()

  const folderTree = computed(() => buildFolderTree(folders.value))

  const currentPath = computed(() => {
    if (!currentFolder.value) return []

    const path: Folder[] = []
    let folder: Folder | null = currentFolder.value

    while (folder) {
      path.unshift(folder)
      folder = folders.value.find((f) => f.id === folder!.parent_folder_id) || null
    }

    return path
  })

  const selectedFilesList = computed(() => files.value.filter((f) => selectedFiles.value.has(f.id)))

  const selectedFoldersList = computed(() =>
    folders.value.filter((f) => selectedFolders.value.has(f.id))
  )

  const hasSelection = computed(
    () => selectedFiles.value.size > 0 || selectedFolders.value.size > 0
  )

  const activeUploads = computed(() =>
    Array.from(uploads.value.values()).filter((u) => u.status !== 'pending')
  )

  function buildFolderTree(flatFolders: Folder[], parentId?: string): Folder[] {
    return flatFolders
      .filter((f) => f.parent_folder_id === (parentId || null) && !f.deleted_at)
      .map((folder) => ({
        ...folder,
        children: buildFolderTree(flatFolders, folder.id)
      }))
      .sort((a, b) => compareFolderNames(a.name, b.name))
  }

  async function loadFiles(params?: {
    folderId?: string
    clientId?: string
    q?: string
    page?: number
    pageSize?: number
  }): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.getFiles(params)
      files.value = response.files
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load files'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loadFolders(params?: {
    parentFolderId?: string
    clientId?: string
  }): Promise<void> {
    loading.value = true
    error.value = null

    try {
      // If loading all folders (no parentFolderId), use tree endpoint
      if (!params?.parentFolderId) {
        const response = await apiService.getFolderTree(params?.clientId)
        // Flatten the tree structure to get all folders
        folders.value = flattenFolderTree(response.tree || [], response.client_id)
      } else {
        // Loading specific folder's children
        const response = await apiService.getFolders(params)
        // Merge with existing folders instead of replacing
        const newFolders = response.folders || []
        const existingIds = new Set(folders.value.map((f) => f.id))
        const foldersToAdd = newFolders.filter((f: any) => !existingIds.has(f.id))
        folders.value = [...folders.value, ...foldersToAdd]
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load folders'
      throw err
    } finally {
      loading.value = false
    }
  }

  function flattenFolderTree(tree: any[], clientId: string): Folder[] {
    const result: Folder[] = []

    function traverse(nodes: any[], parentId?: string) {
      for (const node of nodes) {
        result.push({
          id: node.id,
          name: node.name,
          client_id: clientId,
          parent_folder_id: parentId || undefined,
          direct_files_count: node.direct_files_count || 0,
          direct_folders_count: node.direct_folders_count || 0,
          deleted_at: node.deleted_at || null,
          created_at: node.created_at || new Date().toISOString(),
          updated_at: node.updated_at || new Date().toISOString()
        })

        if (node.children?.length) {
          traverse(node.children, node.id)
        }
      }
    }

    traverse(tree)
    return result
  }

  async function createFolder(
    name: string,
    parentFolderId?: string,
    clientId?: string
  ): Promise<Folder> {
    try {
      const response = await apiService.createFolder(name, parentFolderId, clientId)
      // Backend returns { message: '...', folder: {...} }
      const folder = response.folder || response
      folders.value.push(folder)
      return folder
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create folder'
      throw err
    }
  }

  async function createFolderIfNotExists(
    name: string,
    parentFolderId?: string,
    clientId?: string
  ): Promise<Folder> {
    // Check local state first
    const existing = folders.value.find(
      (f) =>
        f.name === name &&
        f.parent_folder_id === (parentFolderId || undefined) &&
        (!clientId || f.client_id === clientId) &&
        !f.deleted_at
    )
    if (existing) return existing

    try {
      return await createFolder(name, parentFolderId, clientId)
    } catch (err: any) {
      // 409 = folder already exists — re-fetch and return it
      if (err?.response?.status === 409) {
        await loadFolders({ clientId })
        const found = folders.value.find(
          (f) =>
            f.name === name &&
            f.parent_folder_id === (parentFolderId || undefined) &&
            (!clientId || f.client_id === clientId) &&
            !f.deleted_at
        )
        if (found) return found
      }
      throw err
    }
  }

  async function createAccountingFolders(clientId: string, parentFolderId?: string): Promise<any> {
    try {
      const response = await apiService.createAccountingFolderStructure(clientId, parentFolderId)
      // Refresh folder tree to show new folders
      await loadFolders({ clientId })
      return response
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to create accounting folder structure'
      throw err
    }
  }

  async function ensureMonthFolder(
    yearName: string,
    monthName: string,
    clientId?: string
  ): Promise<string> {
    // Ensure we have all folders loaded
    if (clientId) {
      await loadFolders({ clientId })
    }

    // Find or create year folder at root level
    let yearFolder = folders.value.find(
      (f) => f.name === yearName && !f.parent_folder_id && (!clientId || f.client_id === clientId)
    )

    if (!yearFolder) {
      // Create year folder at root
      yearFolder = await createFolder(yearName, undefined, clientId)
      await loadFolders({ clientId }) // Reload to get complete folder structure
    }

    // Find or create month folder within year folder
    let monthFolder = folders.value.find(
      (f) =>
        f.name === monthName &&
        f.parent_folder_id === yearFolder.id &&
        (!clientId || f.client_id === clientId)
    )

    if (!monthFolder) {
      // Create month folder within year folder
      monthFolder = await createFolder(monthName, yearFolder.id, clientId)
      await loadFolders({ clientId }) // Reload to get complete folder structure
    }

    return monthFolder.id
  }

  async function renameFile(fileId: string, newName: string): Promise<void> {
    try {
      const response = await apiService.renameFile(fileId, newName)
      const index = files.value.findIndex((f) => f.id === fileId)
      if (index !== -1) {
        files.value[index] = { ...files.value[index], name: newName }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to rename file'
      throw err
    }
  }

  async function updateFileDescription(fileId: string, description: string): Promise<void> {
    try {
      const response = await apiService.updateFileDescription(fileId, description)
      const index = files.value.findIndex((f) => f.id === fileId)
      if (index !== -1) {
        files.value[index] = { ...files.value[index], description: description || undefined }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update description'
      throw err
    }
  }

  async function renameFolder(folderId: string, newName: string): Promise<void> {
    try {
      const response = await apiService.renameFolder(folderId, newName)
      const index = folders.value.findIndex((f) => f.id === folderId)
      if (index !== -1) {
        folders.value[index] = { ...folders.value[index], name: newName }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to rename folder'
      throw err
    }
  }

  async function moveSelection(targetFolderId?: string, clientId?: string): Promise<void> {
    const fileIds = Array.from(selectedFiles.value)
    const folderIds = Array.from(selectedFolders.value)

    if (fileIds.length === 0 && folderIds.length === 0) return

    // If no clientId provided, try to get it from the first selected file or folder
    if (!clientId) {
      const firstFile = files.value.find((f) => fileIds.includes(f.id))
      const firstFolder = folders.value.find((f) => folderIds.includes(f.id))
      clientId = firstFile?.client_id || firstFolder?.client_id
    }

    try {
      await apiService.moveFiles(fileIds, folderIds, targetFolderId, clientId)

      files.value = files.value.map((f) =>
        fileIds.includes(f.id) ? { ...f, folder_id: targetFolderId } : f
      )

      folders.value = folders.value.map((f) =>
        folderIds.includes(f.id) ? { ...f, parent_folder_id: targetFolderId } : f
      )

      clearSelection()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to move files'
      throw err
    }
  }

  async function deleteSelection(clientId?: string): Promise<void> {
    const fileIds = Array.from(selectedFiles.value)
    const folderIds = Array.from(selectedFolders.value)

    if (fileIds.length === 0 && folderIds.length === 0) return

    // If no clientId provided, try to get it from the first selected file or folder
    if (!clientId) {
      const firstFile = files.value.find((f) => fileIds.includes(f.id))
      const firstFolder = folders.value.find((f) => folderIds.includes(f.id))
      clientId = firstFile?.client_id || firstFolder?.client_id
    }

    try {
      await apiService.deleteFiles(fileIds, folderIds, clientId)

      files.value = files.value.map((f) =>
        fileIds.includes(f.id) ? { ...f, deleted_at: new Date().toISOString() } : f
      )

      folders.value = folders.value.map((f) =>
        folderIds.includes(f.id) ? { ...f, deleted_at: new Date().toISOString() } : f
      )

      clearSelection()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete files'
      throw err
    }
  }

  async function uploadFile(
    file: File,
    folderId?: string,
    clientId?: string,
    onProgress?: (progress: number) => void,
    fromChat?: boolean
  ): Promise<FileItem> {
    const uploadId = crypto.randomUUID()

    uploads.value.set(uploadId, {
      fileId: uploadId,
      filename: file.name,
      progress: 0,
      status: 'pending'
    })

    try {
      // Phase 1: Request presigned URL
      uploads.value.set(uploadId, {
        ...uploads.value.get(uploadId)!,
        status: 'requesting'
      })

      const effectiveClientId =
        clientId || authStore.user?.client?.id || (authStore.isAccountant ? undefined : undefined)

      const { file_id, presigned_url, content_type, filename, was_renamed } =
        await apiService.requestUpload({
          filename: file.name,
          size: file.size,
          mime_type: file.type || 'application/octet-stream',
          client_id: effectiveClientId,
          folder_id: folderId,
          from_chat: fromChat
        })

      // Phase 2: Upload directly to S3
      uploads.value.set(uploadId, {
        ...uploads.value.get(uploadId)!,
        fileId: file_id,
        filename: was_renamed ? filename : file.name,
        status: 'uploading'
      })

      await apiService.uploadToS3(presigned_url, file, content_type, (progress) => {
        uploads.value.set(uploadId, {
          ...uploads.value.get(uploadId)!,
          progress
        })
        onProgress?.(progress)
      })

      // Phase 3: Confirm upload
      uploads.value.set(uploadId, {
        ...uploads.value.get(uploadId)!,
        status: 'confirming',
        progress: 100
      })

      const { file: fileData } = await apiService.confirmUpload(file_id)

      uploads.value.set(uploadId, {
        ...uploads.value.get(uploadId)!,
        status: 'completed',
        progress: 100
      })

      // Add the file to our local state
      files.value.push(fileData)

      // Clean up upload tracking after a short delay
      setTimeout(() => {
        uploads.value.delete(uploadId)
      }, 3000)

      return fileData
    } catch (err) {
      uploads.value.set(uploadId, {
        ...uploads.value.get(uploadId)!,
        status: 'error',
        error: err instanceof Error ? err.message : 'Upload failed'
      })

      // Keep error state longer for user to see
      setTimeout(() => {
        uploads.value.delete(uploadId)
      }, 5000)

      throw err
    }
  }

  async function downloadSelection(): Promise<void> {
    const fileIds = Array.from(selectedFiles.value)
    const folderIds = Array.from(selectedFolders.value)

    if (fileIds.length === 0 && folderIds.length === 0) return

    try {
      let result

      if (fileIds.length === 1 && folderIds.length === 0) {
        // Single file - direct download without ZIP
        result = await apiService.getPresignedDownloadUrl(fileIds[0])
      } else {
        // Multiple files or folders - create ZIP
        result = await apiService.downloadFiles(fileIds, folderIds)
      }

      // The backend returns a presigned URL
      if (result.url) {
        // Open the presigned URL in a new tab to trigger download
        window.open(result.url, '_blank')
      } else {
        throw new Error('No download URL received from server')
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to download files'
      throw err
    }
  }

  function toggleFileSelection(fileId: string): void {
    const next = new Set(selectedFiles.value)
    if (next.has(fileId)) {
      next.delete(fileId)
    } else {
      next.add(fileId)
    }
    selectedFiles.value = next
  }

  function toggleFolderSelection(folderId: string): void {
    const next = new Set(selectedFolders.value)
    if (next.has(folderId)) {
      next.delete(folderId)
    } else {
      next.add(folderId)
    }
    selectedFolders.value = next
  }

  function selectAll(): void {
    const nextFiles = new Set(selectedFiles.value)
    files.value.filter((f) => !f.deleted_at).forEach((f) => nextFiles.add(f.id))
    selectedFiles.value = nextFiles

    const nextFolders = new Set(selectedFolders.value)
    folders.value
      .filter((f) => !f.deleted_at && f.parent_folder_id === currentFolder.value?.id)
      .forEach((f) => nextFolders.add(f.id))
    selectedFolders.value = nextFolders
  }

  function clearSelection(): void {
    selectedFiles.value = new Set()
    selectedFolders.value = new Set()
  }

  function setSelection(fileIds: string[], folderIds: string[]): void {
    selectedFiles.value = new Set(fileIds)
    selectedFolders.value = new Set(folderIds)
  }

  function setCurrentFolder(folder: Folder | null): void {
    currentFolder.value = folder
    clearSelection()
  }

  async function selectFileById(fileId: string): Promise<void> {
    // Clear current selection and select this file
    selectedFolders.value = new Set()
    selectedFiles.value = new Set([fileId])

    // Find the file to get its folder
    const file = files.value.find((f) => f.id === fileId)
    if (file && file.folder_id) {
      // Navigate to the file's folder
      const folder = folders.value.find((f) => f.id === file.folder_id)
      if (folder) {
        setCurrentFolder(folder)
        // Load the folder contents
        await loadFiles({ clientId: file.client_id, folderId: file.folder_id })
      }
    }
  }

  async function loadAvailableClients(): Promise<void> {
    // Only for admin/accountant users
    if (authStore.user?.role === 'user') {
      return
    }

    clientsLoading.value = true
    try {
      const response = await apiService.getClients({ pageSize: 100 })
      availableClients.value = response.clients || []

      // Auto-select first client if none selected
      if (!selectedClientId.value && availableClients.value.length > 0) {
        // Check localStorage for last selected client
        const lastSelectedId = localStorage.getItem('filesSelectedClientId')
        const lastClient = availableClients.value.find((c) => c.id === lastSelectedId)

        if (lastClient) {
          selectedClientId.value = lastClient.id
        } else {
          selectedClientId.value = availableClients.value[0].id
        }
      }
    } catch (err) {
      console.error('Failed to load clients:', err)
      Sentry.captureException(err, { extra: { context: 'load-clients' } })
      error.value = err instanceof Error ? err.message : 'Failed to load clients'
    } finally {
      clientsLoading.value = false
    }
  }

  function selectClient(clientId: string): void {
    // Skip clearing if we're already viewing this client
    if (selectedClientId.value === clientId) return
    selectedClientId.value = clientId
    // Save to localStorage for persistence
    localStorage.setItem('filesSelectedClientId', clientId)
    // Clear current files/folders when switching client
    files.value = []
    folders.value = []
    currentFolder.value = null
    clearSelection()
  }

  function getSelectedClientId(): string | undefined {
    // For regular users, always use their associated client
    if (authStore.user?.role === 'user') {
      return authStore.user.client?.id
    }
    // For admin/accountant, use the selected client
    return selectedClientId.value || undefined
  }

  async function navigateToFile(fileId: string, clientId?: string): Promise<FileItem | undefined> {
    // First, ensure we have all folders loaded
    if (clientId) {
      await loadFolders({ clientId })
    }

    // Load all files to find the target file
    const response = await apiService.getFiles({ clientId })
    const targetFile = response.files?.find((f: FileItem) => f.id === fileId)

    if (targetFile) {
      // Navigate to the file's folder
      if (targetFile.folder_id) {
        const folder = folders.value.find((f) => f.id === targetFile.folder_id)
        if (folder) {
          setCurrentFolder(folder)
        }
      } else {
        // File is at root level
        setCurrentFolder(null)
      }

      // Load files in that folder
      await loadFiles({ clientId: targetFile.client_id, folderId: targetFile.folder_id })

      // Select the file
      selectedFolders.value = new Set()
      selectedFiles.value = new Set([fileId])

      return targetFile
    }

    return undefined
  }

  // File notes management
  const fileNotes = ref<Map<string, FileNote[]>>(new Map())
  const notesLoading = ref(false)
  const notesError = ref<string | null>(null)

  async function loadFileNotes(fileId: string): Promise<FileNote[]> {
    notesLoading.value = true
    notesError.value = null

    try {
      const response = await apiService.getFileNotes(fileId)
      const notes = response.notes || []
      fileNotes.value.set(fileId, notes)
      return notes
    } catch (err) {
      notesError.value = err instanceof Error ? err.message : 'Failed to load notes'
      throw err
    } finally {
      notesLoading.value = false
    }
  }

  async function addFileNote(fileId: string, text: string): Promise<FileNote> {
    notesLoading.value = true
    notesError.value = null

    try {
      const response = await apiService.addFileNote(fileId, text)
      const note = response.note

      // Update local state
      const notes = fileNotes.value.get(fileId) || []
      notes.unshift(note) // Add to beginning since notes are sorted by date desc
      fileNotes.value.set(fileId, notes)

      return note
    } catch (err) {
      notesError.value = err instanceof Error ? err.message : 'Failed to add note'
      throw err
    } finally {
      notesLoading.value = false
    }
  }

  async function loadBulkFileNotes(fileIds: string[]): Promise<void> {
    if (fileIds.length === 0) return

    notesLoading.value = true
    notesError.value = null

    try {
      const response = await apiService.getBulkFileNotes(fileIds)
      const notesByFile = response.notes || {}

      // Update local state for all files
      for (const fileId of fileIds) {
        const notes = notesByFile[fileId] || []
        fileNotes.value.set(fileId, notes)
      }
    } catch (err) {
      notesError.value = err instanceof Error ? err.message : 'Failed to load notes'
      console.error('Failed to load bulk file notes:', err)
      Sentry.captureException(err, { extra: { context: 'load-bulk-file-notes' } })
      // Don't throw - this is not critical, just log the error
    } finally {
      notesLoading.value = false
    }
  }

  function getFileNotes(fileId: string): FileNote[] | undefined {
    return fileNotes.value.get(fileId)
  }

  function clearFileNotes(fileId?: string): void {
    if (fileId) {
      fileNotes.value.delete(fileId)
    } else {
      fileNotes.value.clear()
    }
  }

  async function refreshFile(fileId: string): Promise<void> {
    try {
      // Fetch the latest file data from the server
      const updatedFile = await apiService.getFile(fileId)

      // Find and update the file in our local state
      const index = files.value.findIndex((f) => f.id === fileId)
      if (index !== -1) {
        // Update the file while preserving Vue reactivity
        files.value[index] = updatedFile
      }

      return updatedFile
    } catch (err) {
      console.error('Failed to refresh file:', err)
      Sentry.captureException(err, { extra: { fileId, context: 'refresh-file' } })
      throw err
    }
  }

  return {
    files,
    folders,
    currentFolder,
    selectedFiles,
    selectedFolders,
    uploads,
    loading,
    error,
    selectedClientId,
    availableClients,
    clientsLoading,
    folderTree,
    currentPath,
    selectedFilesList,
    selectedFoldersList,
    hasSelection,
    activeUploads,
    loadFiles,
    loadFolders,
    createFolder,
    createFolderIfNotExists,
    createAccountingFolders,
    ensureMonthFolder,
    renameFile,
    updateFileDescription,
    renameFolder,
    moveSelection,
    deleteSelection,
    uploadFile,
    downloadSelection,
    toggleFileSelection,
    toggleFolderSelection,
    selectAll,
    clearSelection,
    setSelection,
    setCurrentFolder,
    selectFileById,
    navigateToFile,
    loadAvailableClients,
    selectClient,
    getSelectedClientId,
    // File notes
    fileNotes,
    notesLoading,
    notesError,
    loadFileNotes,
    loadBulkFileNotes,
    addFileNote,
    getFileNotes,
    clearFileNotes,
    // File refresh
    refreshFile
  }
})
